import ldFindLastIndex from "lodash-es/findLastIndex";
import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ElementRef,
    inject
} from "@angular/core";
import { EMPTY, merge, Observable, Subject } from "rxjs";
import { auditTime, takeUntil } from "rxjs/operators";

import { LgSimpleChanges } from "@logex/framework/types";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { LgObserveSizeService, SizeEvent } from "@logex/framework/ui-core";
import { observeOnZone } from "@logex/framework/utilities";

import {
    ILevelExpander,
    IPivotTableLevelHeader,
    IPivotTableLevelHeaderDefinition,
    LgPivotTableLevelExpander
} from "./types";
import { LgPivotTableComponent } from "./lg-pivot-table.component";
import { convertPivotLevelHeaders } from "./helpers/convertPivotLevelHeaders";

@Component({
    standalone: false,
    selector: "lg-pivot-table-level-headers",
    templateUrl: "./lg-pivot-table-level-headers.component.html",
    viewProviders: [
        {
            provide: LgPivotTableLevelExpander,
            useExisting: forwardRef(() => LgPivotTableLevelHeadersComponent)
        }
    ],
    host: {
        class: "table__column--header-levels",
        "[class.table__column--header-levels--compact]": "!_areLabelsVisible"
    }
})
export class LgPivotTableLevelHeadersComponent
    implements OnInit, OnChanges, OnDestroy, ILevelExpander
{
    _table = inject(LgPivotTableComponent);
    private _elementRef = inject(ElementRef);
    private _lgTranslate = inject(LgTranslateService);
    private _renderer = inject(Renderer2);
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _observeSize = inject(LgObserveSizeService);
    private _ngZone = inject(NgZone);

    /**
     * Definition of table level header (required).
     */
    @Input({ required: true }) levels!: IPivotTableLevelHeaderDefinition;

    @ViewChild("hiddenLevelsWrapper", { static: true }) _hiddenLevelsWrapper!: ElementRef;

    _hoverLevel: number | null = null;
    _lastVisibleIndex = 0;
    _areLabelsVisible = true;
    _levelsWidth = 0;

    _convertedLevels: Array<IPivotTableLevelHeader | undefined> = [];

    private _width = 0;
    private _destroyed$ = new Subject<void>();
    private _expandActionLabels: { expand: string; collapse: string } | null = null;

    constructor() {
        this._table
            ._hoverLevelObservable()
            .pipe(takeUntil(this._destroyed$))
            .subscribe(level => {
                this._hoverLevel = level;
            });
    }

    ngOnInit(): void {
        const parent = this._elementRef.nativeElement.parentElement;

        // Width can come from this element or its parent; the hidden wrapper gives required label width.
        // Resize is observed outside Angular zone and batched, so one resize triggers one CD run.
        merge(
            this._observeWidth(this._elementRef),
            parent ? this._observeWidth(new ElementRef(parent)) : EMPTY,
            this._observeWidth(this._hiddenLevelsWrapper)
        )
            .pipe(auditTime(0), observeOnZone(this._ngZone), takeUntil(this._destroyed$))
            .subscribe(() => {
                this._onLevelsChange();
                this._onViewPortChange();
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnChanges(changes: LgSimpleChanges<LgPivotTableLevelHeadersComponent>): void {
        if (changes.levels) {
            this._convertLevels();
        }
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    getAllExpanded(level: number): boolean {
        return this._table.getAllExpanded(level);
    }

    toggleLevel(level: number, expanded?: boolean): void {
        if (!this._table.data) return;

        const expand = expanded ?? !this.getAllExpanded(level);
        if (expand) {
            // Expand any collapsed levels above, otherwise the change wouldn't be visible
            for (let i = this._table.maxVisibleLevel; i < level; ++i) {
                this._table.toggleLevel(i, true);
            }
        }
        this._table.toggleLevel(level, expand);
    }

    _toggleNameLevel(level: number, $event: MouseEvent): void {
        $event.stopPropagation();
        $event.preventDefault();

        if (!this._table.data) return;

        const last = level === this._lastVisibleIndex;
        if (this._table.maxVisibleLevel < level) {
            for (let i = this._table.maxVisibleLevel; i < level; ++i) {
                this._table.toggleLevel(i, true);
            }
        }
        if (!last) {
            this._table.toggleLevel(level);
        }
    }

    _sortByDisabled(
        levelDef: IPivotTableLevelHeader,
        _isTheVisibleRow: boolean,
        index: number
    ): boolean {
        const emptyTable = this._table.passive ? !this._table.filteredData : !this._table.data;

        return !levelDef.orderBy || this._table.maxVisibleLevel < index || emptyTable;
    }

    _levelLabelVisible(isTheVisibleRow: boolean, index: number): boolean {
        return !isTheVisibleRow || this._areLabelsVisible || this._table.maxVisibleLevel === index;
    }

    _expandTooltip(index: number): string {
        const levelDef = this._convertedLevels[index];

        if (!levelDef) {
            return "";
        }

        const expanded = this._table.getAllExpanded(index);
        const action = this._getExpandActionLabel(expanded);

        return levelDef.header ? `${action} ${levelDef.header}` : action;
    }

    _showDotForLastLevel(isTheVisibleRow: boolean, index: number): boolean {
        return isTheVisibleRow && !this._areLabelsVisible && index === this._lastVisibleIndex;
    }

    private _observeWidth(elementRef: ElementRef): Observable<SizeEvent> {
        return this._observeSize
            .observe(elementRef, this._renderer)
            .change({ type: "width", auditTime: 0, outsideZone: true });
    }

    private _getExpandActionLabel(expanded: boolean): string {
        if (this._expandActionLabels == null) {
            this._expandActionLabels = {
                expand: this._lgTranslate.translate("FW._Common.Expand"),
                collapse: this._lgTranslate.translate("FW._Common.Collapse")
            };
        }

        return expanded ? this._expandActionLabels.collapse : this._expandActionLabels.expand;
    }

    private _convertLevels(): void {
        this._expandActionLabels = null;
        this._convertedLevels = convertPivotLevelHeaders(this.levels, this._lgTranslate);
        this._lastVisibleIndex = ldFindLastIndex(
            this._convertedLevels,
            l => l != null && l.hidden !== true
        );
    }

    private _onLevelsChange(): void {
        if (this._hiddenLevelsWrapper) {
            const newWidth = this._hiddenLevelsWrapper.nativeElement.clientWidth;
            if (newWidth !== this._levelsWidth) {
                this._levelsWidth = newWidth;
                this._setLabelsVisibility();
            }
        }
    }

    private _onViewPortChange(): void {
        if (this._elementRef.nativeElement) {
            const width =
                this._elementRef.nativeElement.style.width === ""
                    ? this._elementRef.nativeElement.parentElement.clientWidth
                    : this._elementRef.nativeElement.clientWidth;

            if (width !== this._width) {
                this._width = width;
                this._setLabelsVisibility();
            }
        }
    }

    private _setLabelsVisibility(): void {
        if (!this._levelsWidth || !this._width) return;

        this._areLabelsVisible = this._levelsWidth < this._width;
    }
}
